/**
 * guestServerResearch — frontend client for the server-side durable GUEST
 * deep-research path (Point 6, Option B).
 *
 * ─────────────────────────────────────────────────────────────────────────
 * WHAT THIS REPLACES (guest surface only)
 * ─────────────────────────────────────────────────────────────────────────
 * On the unauthenticated homepage/guest surface, the legacy deepResearchTool
 * runs the whole multi-stage Perplexity → synthesis → deliverable pipeline in
 * the browser JS heap, so a hard refresh kills it. This module instead:
 *   1. POSTs the prompt to `guestStartDeepResearch` → { taskId } (HTTP 202).
 *   2. Persists that taskId onto the in-flight assistant message
 *      (message.serverTaskId) so it survives a reload.
 *   3. Polls `guestPollTask` (backoff) updating the stage widget.
 *   4. On `complete`, returns the final memo + serialized PDF/DOCX deliverables
 *      so the caller can finalize via the SAME stream.finalize() render path.
 *
 * On hard-refresh mid-run, useGlobalChat re-attaches by calling
 * `pollGuestTaskUntilDone(serverTaskId)` again — the server task kept running.
 *
 * The MIGRATED/paid-account path never touches this module (it uses
 * prest0nRunTurn). Detection is the host's job (guest === no migrated session).
 */

import type { SerializedAttachment } from '../../components/GlobalChatSystem/types';

// Direct Cloud Function URLs — bypass Firebase Hosting's 60s rewrite timeout,
// matching PERPLEXITY_PROXY_URL_DIRECT in perplexityClient.ts. The poll calls
// are short, but start/poll share the same base for consistency.
const FUNCTIONS_BASE = 'https://us-central1-tensile-tenure-453409-k3.cloudfunctions.net';
export const GUEST_START_URL = `${FUNCTIONS_BASE}/guestStartDeepResearch`;
export const GUEST_POLL_URL = `${FUNCTIONS_BASE}/guestPollTask`;

export interface GuestServerResearchStage {
  label: string;
  stage: string;
  payload?: unknown;
}

export interface GuestServerResearchResult {
  report: string;
  title?: string;
  attachments: SerializedAttachment[];
  metadata?: Record<string, unknown>;
}

export type GuestTaskStatus = 'pending' | 'running' | 'complete' | 'error' | 'not_found' | 'timeout';

export interface GuestPollResponse {
  taskId: string;
  status: GuestTaskStatus;
  stages: GuestServerResearchStage[];
  currentStage: GuestServerResearchStage | null;
  result: GuestServerResearchResult | null;
  error: string | null;
}

export class GuestResearchRateLimitError extends Error {
  retryAfterSec?: number;
  constructor(message: string, retryAfterSec?: number) {
    super(message);
    this.name = 'GuestResearchRateLimitError';
    this.retryAfterSec = retryAfterSec;
  }
}

/**
 * PRE-FLIGHT STAMP (stamp-race fix, 2026-07-10): mint a guest task id
 * CLIENT-SIDE in the exact entropy shape the backend's generateGuestTaskId
 * produces (`gtask_<base36ts>_<hex>`; the id is the ONLY credential for
 * polling, so it must be unguessable — 96 bits here). Stamped onto the
 * pending widget as serverTaskId BEFORE the start request, then sent as
 * body.clientTaskId so a refresh during the request/202 window still
 * re-attaches. Falls back to the server-minted id when unsupported.
 */
export function mintGuestClientTaskId(): string {
  const ts = Date.now().toString(36);
  const bytes = new Uint8Array(12);
  crypto.getRandomValues(bytes);
  const rand = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `gtask_${ts}_${rand}`;
}

/**
 * Kick off a server-side guest deep-research task. Returns the unguessable
 * taskId. Throws GuestResearchRateLimitError on 429.
 */
export async function startGuestDeepResearch(params: {
  prompt: string;
  language?: 'en' | 'es';
  /** Optional client-pre-analyzed attachment text. */
  documentContent?: string;
  signal?: AbortSignal;
  /** Pre-flight client-minted id (see mintGuestClientTaskId). */
  clientTaskId?: string;
}): Promise<{ taskId: string }> {
  const res = await fetch(GUEST_START_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      prompt: params.prompt,
      ...(params.language ? { language: params.language } : {}),
      ...(params.documentContent ? { documentContent: params.documentContent } : {}),
      ...(params.clientTaskId ? { clientTaskId: params.clientTaskId } : {}),
    }),
    signal: params.signal,
  });

  if (res.status === 429) {
    let retryAfterSec: number | undefined;
    try {
      const body = await res.json();
      retryAfterSec = typeof body?.retryAfterSec === 'number' ? body.retryAfterSec : undefined;
    } catch {
      /* ignore */
    }
    throw new GuestResearchRateLimitError(
      'Too many research requests right now. Please try again in a little while.',
      retryAfterSec,
    );
  }

  if (!res.ok) {
    let msg = `start failed (${res.status})`;
    try {
      const body = await res.json();
      if (body?.error) msg = String(body.error);
    } catch {
      /* ignore */
    }
    throw new Error(msg);
  }

  const data = await res.json();
  const taskId = typeof data?.taskId === 'string' ? data.taskId : '';
  if (!taskId) throw new Error('start did not return a taskId');
  return { taskId };
}

/** Single poll of a guest task. */
export async function pollGuestTask(
  taskId: string,
  signal?: AbortSignal,
): Promise<GuestPollResponse> {
  const url = `${GUEST_POLL_URL}?taskId=${encodeURIComponent(taskId)}`;
  const res = await fetch(url, { method: 'GET', signal });

  if (res.status === 404) {
    return { taskId, status: 'not_found', stages: [], currentStage: null, result: null, error: 'Task not found' };
  }
  if (!res.ok) {
    throw new Error(`poll failed (${res.status})`);
  }

  const data = await res.json();
  return {
    taskId,
    status: (data?.status as GuestTaskStatus) || 'pending',
    stages: Array.isArray(data?.stages) ? data.stages : [],
    currentStage: data?.currentStage || null,
    result: data?.result || null,
    error: data?.error || null,
  };
}

const TERMINAL_STATUSES: ReadonlySet<GuestTaskStatus> = new Set(['complete', 'error', 'not_found']);

export function isTerminalGuestStatus(status: GuestTaskStatus): boolean {
  return TERMINAL_STATUSES.has(status);
}

/**
 * Poll a guest task to completion with linear-then-capped backoff. Calls
 * `onStage` on every NEW stage transition (deduped by stage+label) so the
 * caller can update the stage widget. Resolves with the terminal poll
 * response (status complete | error | not_found).
 *
 * Pure-ish: the only side effect is fetch + the onStage callback. The host
 * passes an AbortSignal so a cancel/unmount tears the loop down cleanly.
 */
export async function pollGuestTaskUntilDone(
  taskId: string,
  opts: {
    onStage?: (stage: GuestServerResearchStage) => void;
    signal?: AbortSignal;
    /** Max wall-clock before giving up (default 10 min). */
    maxWaitMs?: number;
    /** Initial poll interval (default 2s), capped at 6s. */
    initialIntervalMs?: number;
  } = {},
): Promise<GuestPollResponse> {
  // NON-DESTRUCTIVE EXPIRY (2026-07-10): sonar-deep-research legitimately
  // runs 10-15+ minutes, so the old 10-min default routinely mislabeled a
  // still-running task as an error. Default raised to 30 min, and expiry now
  // returns the distinct 'timeout' status — the SERVER task is still running;
  // callers must keep the widget resumable (serverTaskId intact) instead of
  // finalizing an error/interrupted card.
  const maxWaitMs = opts.maxWaitMs ?? 30 * 60 * 1000;
  const startedAt = Date.now();
  let interval = opts.initialIntervalMs ?? 2000;
  const maxInterval = 6000;
  let lastStageKey = '';

  // First poll immediately so re-attach (refresh) shows current state fast.
  // eslint-disable-next-line no-constant-condition
  while (true) {
    if (opts.signal?.aborted) {
      throw new DOMException('Guest research polling aborted', 'AbortError');
    }

    const resp = await pollGuestTask(taskId, opts.signal);

    // Surface the latest stage (deduped) for the widget.
    const cur = resp.currentStage;
    if (cur && opts.onStage) {
      const key = `${cur.stage}::${cur.label}`;
      if (key !== lastStageKey) {
        lastStageKey = key;
        try {
          opts.onStage(cur);
        } catch {
          /* never let a UI callback break the loop */
        }
      }
    }

    if (isTerminalGuestStatus(resp.status)) {
      return resp;
    }

    if (Date.now() - startedAt > maxWaitMs) {
      return {
        ...resp,
        status: 'timeout',
        error: 'Research is taking longer than expected — it is still running on the server.',
      };
    }

    await delay(interval, opts.signal);
    interval = Math.min(maxInterval, Math.round(interval * 1.3));
  }
}

function delay(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const t = setTimeout(resolve, ms);
    if (signal) {
      const onAbort = () => {
        clearTimeout(t);
        reject(new DOMException('aborted', 'AbortError'));
      };
      if (signal.aborted) onAbort();
      else signal.addEventListener('abort', onAbort, { once: true });
    }
  });
}
